import { useI18n, useSlotText } from "@/i18n/translations";
import type { Slot } from "@/i18n/translations";
import { Seo } from "@/components/Seo";
import { DemoBadge, NoteCard } from "@/components/Editorial";
import { Reveal } from "@/components/Reveal";
import { SlotText } from "@/components/Editorial";

type LegalSection = { title: Slot; text: Slot };

const privacySections: LegalSection[] = [
  {
    title: { it: "Titolare del trattamento", en: "Data controller" },
    text: { it: "Centro Studi Pedagogici Don Lorenzo Milani — Genova. Riferimenti di contatto: info@cspdlm.it.", en: null },
  },
  {
    title: { it: "Dati trattati", en: "Data processed" },
    text: { it: "Il form «Contatti» raccoglie esclusivamente i dati inseriti dall'utente: nome, indirizzo email, oggetto e contenuto del messaggio.", en: null },
  },
  {
    title: { it: "Finalità e base giuridica", en: "Purposes and legal basis" },
    text: { it: "I dati sono utilizzati al solo fine di rispondere alle richieste ricevute tramite il form dei contatti.", en: null },
  },
  {
    title: { it: "Modalità di trattamento e conservazione", en: "Processing and retention" },
    text: { it: "I messaggi sono recapitati via posta elettronica alla casella della segreteria. Il sito, essendo interamente statico, non archivia i dati in un database.", en: null },
  },
  {
    title: { it: "Diritti dell'interessato", en: "Data subject rights" },
    text: { it: "L'interessato può esercitare in ogni momento i diritti previsti dal Regolamento UE 2016/679 (accesso, rettifica, cancellazione, opposizione e altri) scrivendo ai riferimenti di contatto del titolare.", en: null },
  },
  {
    title: { it: "Documento definitivo", en: "Final document" },
    text: { it: "Questa informativa è un segnaposto: sarà sostituita dal documento definitivo generato tramite LegalBlink o equivalente.", en: null },
  },
];

const cookieSections: LegalSection[] = [
  {
    title: { it: "Cosa sono i cookie", en: "What cookies are" },
    text: { it: "I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente. [Segnaposto — testo definitivo a cura del Centro.]", en: null },
  },
  {
    title: { it: "Cookie utilizzati da questo sito", en: "Cookies used by this site" },
    text: { it: "Questo sito è interamente statico e non utilizza cookie di profilazione, statistici o di tracciamento. [Segnaposto — da confermare con il documento definitivo.]", en: null },
  },
  {
    title: { it: "Cookie di terze parti", en: "Third-party cookies" },
    text: { it: "Al momento non vengono installati cookie di terze parti. [Slot riservato a eventuali integrazioni future, ad esempio LegalBlink.]", en: null },
  },
  {
    title: { it: "Gestione delle preferenze", en: "Managing preferences" },
    text: { it: "L'utente può gestire o eliminare i cookie dalle impostazioni del proprio browser. [Segnaposto — testo definitivo a cura del Centro.]", en: null },
  },
];

const LegalDoc = ({ titleKey, seoKey, sections, testid }: { titleKey: string; seoKey: string; sections: LegalSection[]; testid: string }) => {
  const { t } = useI18n();
  const slotText = useSlotText();
  return (
    <>
      <Seo titleKey={seoKey} descKey={`${seoKey.split(".")[0]}.desc`} path={`/${titleKey === "footer.privacy" ? "privacy" : "cookie-policy"}`} />
      <header className="border-b border-borderc bg-cardwhite">
        <div className="container-x py-14 sm:py-20" data-testid={testid}>
          <Reveal>
            <div className="flex flex-wrap items-center gap-4">
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">{t(titleKey)}</h1>
              <DemoBadge />
            </div>
            <p className="mt-4 text-xs uppercase tracking-[0.14em] text-mutedtx">{t("legal.updated")}</p>
          </Reveal>
        </div>
      </header>
      <div className="container-x max-w-3xl space-y-10 py-14 sm:py-20">
        <Reveal>
          <NoteCard title={t("att.nota_title")}>{t("legal.placeholder_note")}</NoteCard>
        </Reveal>
        {sections.map((s, i) => (
          <Reveal key={i} delay={Math.min(i * 0.04, 0.2)}>
            <section className="border-t border-borderc pt-8">
              <h2 className="font-serif text-2xl font-medium text-ink">
                {i + 1}. {slotText(s.title).text}
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-bodytx/85">
                <SlotText slot={s.text} />
              </p>
            </section>
          </Reveal>
        ))}
      </div>
    </>
  );
};

export const PrivacyPage = () => (
  <LegalDoc titleKey="footer.privacy" seoKey="seo.privacy.title" sections={privacySections} testid="privacy-page" />
);

export const CookiePolicyPage = () => (
  <LegalDoc titleKey="footer.cookie" seoKey="seo.cookie.title" sections={cookieSections} testid="cookie-policy-page" />
);
